import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export interface Notificacion {
  id: string;
  type: string;
  title: string;
  body: string;
  data: Record<string, unknown> | null;
  readAt: string | null;
  createdAt: string;
}

/**
 * Cuántas hay sin leer.
 *
 * Va aparte de la lista a propósito: es lo único que necesita el punto rojo de
 * la campana, y se consulta en cada pantalla. Traer las veinte últimas para
 * pintar un número sería pedirle a la base de datos veinte veces más de lo
 * necesario.
 */
export function useSinLeer(activo: boolean) {
  return useQuery({
    queryKey: ['notificaciones', 'sin-leer'],
    queryFn: async () =>
      (await api.get<{ total: number }>('/notificaciones/sin-leer')).data.total,
    enabled: activo,
    // El número también llega por el canal en vivo; esto es la red de
    // seguridad para pestañas que estuvieron dormidas.
    refetchInterval: activo ? 120_000 : false,
  });
}

export function useNotificaciones(activo: boolean) {
  return useQuery({
    queryKey: ['notificaciones', 'lista'],
    queryFn: async () =>
      (
        await api.get<{ items: Notificacion[]; nextCursor: string | null }>(
          '/notificaciones?limit=20',
        )
      ).data,
    enabled: activo,
  });
}

export function useMarcarLeida() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<{ sinLeer: number }>(`/notificaciones/${id}/leida`)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notificaciones'] }),
  });
}

export function useLeerTodas() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async () =>
      (await api.post<{ sinLeer: number }>('/notificaciones/leer-todas')).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notificaciones'] }),
  });
}
